/**
 * Pending-invitations panel.
 *
 * Renders the rows returned by GET /api/invitations/me. Hidden when there
 * are no pending invitations (passes nothing to the page in that case).
 *
 * On accept: hits POST /api/invitations/:token/accept and notifies the
 * parent via onAccepted so the apps list can be refreshed.
 */

import React, { useState } from 'react';
import { useApi } from '../lib/api';

const ROLE_LABELS = { admin: 'Admin', member: 'Member' };

export default function InvitationsPanel({ invitations, onAccepted, onDeclined, onError }) {
  const api = useApi();
  const [busy, setBusy] = useState(null); // token currently being processed

  if (!invitations || invitations.length === 0) return null;

  const handleAccept = async (token) => {
    setBusy(token);
    try {
      const result = await api.acceptInvitation(token);
      onAccepted?.(result);
    } catch (err) {
      onError?.(
        err.response?.data?.error || err.message || 'Failed to accept invitation',
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDecline = async (token) => {
    setBusy(`decline-${token}`);
    try {
      await api.declineInvitation(token);
      onDeclined?.();
    } catch (err) {
      onError?.(
        err.response?.data?.error || err.message || 'Failed to decline invitation',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="invitations-panel">
      <h2 className="section-title">
        Pending Invitations <span className="badge-count">{invitations.length}</span>
      </h2>
      <div className="invitations-list">
        {invitations.map((inv) => (
          <div key={inv.id} className="invitation-row">
            <div className="invitation-meta">
              <p className="invitation-app">
                <strong>{inv.app_name}</strong>
                <span className="muted"> · {inv.app_type.toUpperCase()}</span>
              </p>
              <p className="invitation-from">
                Invited by{' '}
                <strong>
                  {[inv.invited_by_first_name, inv.invited_by_last_name]
                    .filter(Boolean)
                    .join(' ') || inv.invited_by_email}
                </strong>{' '}
                <span className="muted">·</span>{' '}
                Role: <strong>{ROLE_LABELS[inv.role] || inv.role}</strong>
              </p>
            </div>
            <div className="invitation-actions">
              <button
                className="btn-primary"
                onClick={() => handleAccept(inv.token)}
                disabled={busy === inv.token || busy === `decline-${inv.token}`}
              >
                {busy === inv.token ? 'Accepting…' : 'Accept'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => handleDecline(inv.token)}
                disabled={busy === inv.token || busy === `decline-${inv.token}`}
              >
                {busy === `decline-${inv.token}` ? 'Declining…' : 'Decline'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
